/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './MultiSelectDropdown.css';
import { Icon } from './shared/ComponentIcon';
import { AppLogo } from './shared/AppLogo';
import { QubeproMediaImg } from './shared/QubeproMediaImg';
import type { DmsMediaUrlOptions } from '../dms';
import { useTheme } from '../context/ThemeContext';
import { useFloatingDropdownMenu } from '../hooks/useFloatingDropdownMenu';

export interface MultiSelectOption {
  value: string;
  label: string;
  searchStr?: string;
  group?: string;
  description?: string;
  icon?: string;
  /** Trusted SVG from the canonical application registry. */
  iconSvg?: string;
  color?: string;
  colorDark?: string;
  mediaStored?: string | null;
  mediaCompanyScope?: DmsMediaUrlOptions['companyScope'];
  mediaSystemMedia?: boolean;
  mediaAlt?: string;
  mediaFallbackText?: string;
}

interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  id?: string;
  selectedCountLabel?: (count: number) => string;
  showSelectAll?: boolean;
  selectAllLabel?: string;
  clearSelectionLabel?: string;
  noResultsText?: string;
  onSearchChange?: (value: string) => void;
}

function getOptionIconColor(theme: string, color?: string, colorDark?: string): string {
  if (!color) return 'var(--text-secondary)';
  if (theme !== 'dark') return color;
  return colorDark ?? color;
}

export const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Seçiniz...',
  disabled = false,
  searchable = true,
  searchPlaceholder = 'Ara...',
  id,
  selectedCountLabel,
  showSelectAll = true,
  selectAllLabel = 'Tümünü seç',
  clearSelectionLabel,
  noResultsText = 'Sonuç bulunamadı',
  onSearchChange,
}) => {
  const { theme } = useTheme();
  const generatedId = useId();
  const triggerId = id ?? `${generatedId}-trigger`;
  const panelId = `${generatedId}-options`;
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { dropdownRef, dropdownStyle } = useFloatingDropdownMenu<HTMLDivElement>({
    open: isOpen,
    anchorRef: wrapperRef,
    minWidth: 260,
    minHeight: 160,
    maxHeight: 320,
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const lower = searchTerm.toLowerCase();
    return options.filter((option) => {
      const haystack = [
        option.searchStr ?? option.label,
        option.description ?? '',
        option.group ?? '',
      ].join(' ').toLowerCase();
      return haystack.includes(lower);
    });
  }, [options, searchTerm]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const filteredValues = useMemo(() => filteredOptions.map((option) => option.value), [filteredOptions]);
  const allFilteredSelected = filteredValues.length > 0 && filteredValues.every((item) => selectedSet.has(item));
  const groupedOptions = useMemo(() => {
    const groups: Array<{ label: string; options: MultiSelectOption[] }> = [];
    filteredOptions.forEach((option) => {
      const label = option.group || '';
      let group = groups.find((item) => item.label === label);
      if (!group) {
        group = { label, options: [] };
        groups.push(group);
      }
      group.options.push(option);
    });
    return groups;
  }, [filteredOptions]);

  const handleToggle = (optionValue: string) => {
    if (selectedSet.has(optionValue)) {
      onChange(value.filter((item) => item !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      onChange(value.filter((item) => !filteredValues.includes(item)));
    } else {
      const merged = new Set([...value, ...filteredValues]);
      onChange(Array.from(merged));
    }
  };

  useEffect(() => {
    if (isOpen && dropdownStyle.visibility === 'visible') {
      const firstControl = searchable ? inputRef.current : dropdownRef.current?.querySelector<HTMLElement>('button, input');
      (firstControl ?? dropdownRef.current)?.focus();
    }
    if (!isOpen) {
      setSearchTerm('');
      onSearchChange?.('');
    }
  }, [isOpen, onSearchChange, searchable, dropdownRef, dropdownStyle.visibility]);

  useEffect(() => { if (disabled) setIsOpen(false); }, [disabled]);

  const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setIsOpen(false);
      triggerRef.current?.focus();
    } else if (event.key === 'Tab') {
      const controls = Array.from(dropdownRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)') ?? []);
      const boundary = event.shiftKey ? controls[0] : controls[controls.length - 1];
      if (!controls.length || document.activeElement === boundary) {
        // Resume the native tab order at the anchor, including the containing
        // Core modal's boundary handling. The portal must not strand focus.
        setIsOpen(false);
        triggerRef.current?.focus();
      } else {
        // Let native Tab traverse checkboxes without the parent modal treating
        // this portalled control as focus outside its own surface.
        event.stopPropagation();
      }
    }
  };

  const triggerLabel =
    value.length === 0
      ? placeholder
      : selectedCountLabel
        ? selectedCountLabel(value.length)
        : `${value.length} seçili`;

  const dropdownContent = isOpen && (
    <div
      ref={dropdownRef}
      id={panelId}
      role="dialog"
      aria-modal={false}
      aria-labelledby={triggerId}
      tabIndex={-1}
      onKeyDown={handlePanelKeyDown}
      onBlur={(event) => {
        const next = event.relatedTarget as Node | null;
        // Some browsers do not focus buttons on pointer activation. A null
        // relatedTarget is not evidence of leaving the picker: closing here
        // can discard the pending click or reopen the trigger. Outside pointer
        // clicks and keyboard Tab boundaries already dismiss the panel.
        if (next && !event.currentTarget.contains(next) && !wrapperRef.current?.contains(next)) setIsOpen(false);
      }}
      className="qubepro-multiselect-dropdown multiselect-dropdown-portal"
      style={dropdownStyle}
    >
      {searchable && (
        <div className="multiselect-dropdown-search">
          <Icon name="search" size={16} className="multiselect-dropdown-search-icon" />
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              onSearchChange?.(event.target.value);
            }}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            onClick={(event) => event.stopPropagation()}
            autoComplete="off"
          />
        </div>
      )}
      <div className="multiselect-dropdown-options">
        {showSelectAll && filteredOptions.length > 0 && (
          <button
            type="button"
            className="multiselect-dropdown-select-all"
            onClick={handleSelectAll}
          >
            {allFilteredSelected && clearSelectionLabel ? clearSelectionLabel : selectAllLabel}
          </button>
        )}
        {filteredOptions.length > 0 ? (
          groupedOptions.map((group) => (
            <div key={group.label || '__default'} className="multiselect-dropdown-group">
              {group.label && (
                <div className="multiselect-dropdown-group-title">{group.label}</div>
              )}
              {group.options.map((option) => {
                const checked = selectedSet.has(option.value);
                const iconColor = getOptionIconColor(theme, option.color, option.colorDark);
                return (
                  <label key={option.value} className="multiselect-dropdown-option">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggle(option.value)}
                    />
                    {option.mediaStored || option.mediaFallbackText ? (
                      <span className="multiselect-dropdown-option-media">
                        {option.mediaStored ? (
                          <QubeproMediaImg
                            stored={option.mediaStored}
                            companyScope={option.mediaCompanyScope}
                            systemMedia={option.mediaSystemMedia}
                            alt={option.mediaAlt ?? option.label}
                            className="multiselect-dropdown-option-media-image"
                          />
                        ) : (
                          <span className="multiselect-dropdown-option-media-fallback" aria-hidden>
                            {option.mediaFallbackText}
                          </span>
                        )}
                      </span>
                    ) : (option.iconSvg || option.icon) && (
                      <span
                        className="multiselect-dropdown-option-icon"
                        style={{ '--option-icon-color': iconColor } as React.CSSProperties}
                      >
                        <AppLogo app={option} size={18} color={iconColor} />
                      </span>
                    )}
                    <span className="multiselect-dropdown-option-label">
                      <span>{option.label}</span>
                      {option.description && <small>{option.description}</small>}
                    </span>
                  </label>
                );
              })}
            </div>
          ))
        ) : (
          <div className="multiselect-dropdown-no-results">{noResultsText}</div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <div
        className={`qubepro-multiselect-dropdown multiselect-dropdown-trigger ${disabled ? 'disabled' : ''} ${isOpen ? 'is-open' : ''}`}
        ref={wrapperRef}
      >
        <button
          type="button"
          id={triggerId}
          ref={triggerRef}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-controls={isOpen ? panelId : undefined}
          className="multiselect-dropdown-trigger-inner"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className={value.length === 0 ? 'placeholder' : ''}>{triggerLabel}</span>
          <Icon name="expandMore" size={16} className="multiselect-dropdown-arrow" />
        </button>
      </div>
      {typeof document !== 'undefined' && dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
};
